import { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { getTranslation, getCoordinatesByW3wInfo } from "./Utility.js";

/**
 * A component to get a list of stories
 * @param {object} props
 * @param {object} props.url - The url that changes
 * @param {object} props.searchTerm - The term to search for
 * @returns {object} A list of stories and loading value
 */
const useStories = (url, searchTerm) => {
  const url_backend = process.env.REACT_APP_URL_BACKEND;
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);

  /**
   * A function to search a list of google places
   * @param {object} query - Object query to find places
   * @returns {object} A list of places
   */
  const findPlaces = async (query) => {
    let data = [];
    const request = {
      textQuery: query,
      fields: ["displayName", "location", "businessStatus", "formattedAddress"],
      isOpenNow: true,
      language: "it-IT",
      maxResultCount: 8,
      minRating: 3.2,
      region: "it",
      useStrictTypeFiltering: false,
    };
    //@ts-ignore
    const { places } = await window.google.maps.places.Place.searchByText(
      request
    );
    if (places.length) {
      console.log(places);
      // Loop through and get all the results.
      places.forEach((place) => {
        //bounds.extend(place.location);
        data.push({
          id: uuidv4(),
          name: place.displayName,
          address: place.formattedAddress
            ? place.formattedAddress
            : place.displayName,
          emoji: "🕸️",
          position: place.location.toJSON(),
        });
      });

      return data;
    } else {
      console.log("No results");
    }
  };

  // Set loading state or story state from cached data
  useEffect(() => {
    setLoading(true);
    if (!searchTerm) {
      setLoading(false);
      return setStories([]);
    }

    const cachedData = sessionStorage.getItem(`stories_${searchTerm}`);
    if (cachedData) {
      setStories(JSON.parse(cachedData));
      setLoading(false);
      return;
    }

    /**
     * A function that performs multiple searches by text term state
     * @returns {void} Set variables of state
     */
    const getStories = setTimeout(async () => {
      try {
        let data = [];

        var regex =
          /^\/{0,}(?:[^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+[.｡。･・︒។։။۔።।][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+[.｡。･・︒។։။۔።।][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+|[^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+([\u0020\u00A0][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+){1,3}[.｡。･・︒។։။۔።।][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+([\u0020\u00A0][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+){1,3}[.｡。･・︒។։။۔።।][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+([\u0020\u00A0][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+){1,3})$/;
        if (regex.test(searchTerm.toLowerCase())) {
          try {
            const responseW3w = await fetch(
              url_backend + `offers/searchw3w/${searchTerm.toLowerCase()}`
            );
            // Check for (status 200-299)
            if (!responseW3w.ok) {
              throw new Error(
                `HTTP error! Status: ${responseW3w.status} - ${responseW3w.statusText}`
              );
            }
            const placeW3w = await responseW3w.json();
            if (placeW3w.length > 0) {
              data.push({
                id: uuidv4(),
                name: placeW3w[0].w3wData.address,
                address: "",
                emoji: "🔳",
                position: {
                  lat: placeW3w[0].position.coordinates[1],
                  lng: placeW3w[0].position.coordinates[0],
                },
              });
            }
          } catch (error) {
            console.error(
              "Si è verificato un errore durante la chiamata search w3w:",
              error.message
            );
          }

          const coordinatesFromW3w = await getCoordinatesByW3wInfo(
            searchTerm.toLowerCase()
          );
          data.push({
            id: uuidv4(),
            name: searchTerm.toLowerCase(),
            address: "",
            emoji: "🇼",
            position: {
              lat: coordinatesFromW3w.coordinates.lat,
              lng: coordinatesFromW3w.coordinates.lng,
            },
          });

          console.log(coordinatesFromW3w);
        }

        // It also check if the search tearm is part of a category
        try {
          const responseCategory = await fetch(
            url_backend + `offers/searchcategory/${searchTerm}/20`
          );

          // Check for (status 200-299)
          if (!responseCategory.ok) {
            throw new Error(
              `HTTP error! Status: ${responseCategory.status} - ${responseCategory.statusText}`
            );
          }
          const placesCategory = await responseCategory.json();
          if (placesCategory.length > 0) {
            placesCategory.forEach((place) => {
              data.push({
                id: uuidv4(),
                name: place.name,
                address: getTranslation(place.category),
                emoji: "🏠",
                position: {
                  lat: place.position.coordinates[1],
                  lng: place.position.coordinates[0],
                },
              });
            });
          }
        } catch (error) {
          console.error(
            "Si è verificato un errore durante la chiamata search category:",
            error.message
          );
        }

        // It also check if the search tearm is part of a name
        try {
          const responseName = await fetch(
            url_backend + `offers/searchname/${searchTerm}/20`
          );

          // Check for (status 200-299)
          if (!responseName.ok) {
            throw new Error(
              `HTTP error! Status: ${responseName.status} - ${responseName.statusText}`
            );
          }
          const placesName = await responseName.json();
          if (placesName.length > 0) {
            placesName.forEach((place) => {
              data.push({
                id: uuidv4(),
                name: place.name,
                address: getTranslation(place.category),
                emoji: "🏠",
                position: {
                  lat: place.position.coordinates[1],
                  lng: place.position.coordinates[0],
                },
              });
            });
          }
        } catch (error) {
          console.error(
            "Si è verificato un errore durante la chiamata search category:",
            error.message
          );
        }

        // Finally search with google maps api
        /* const dataGoogle = await findPlaces(searchTerm);
                data = data.concat(dataGoogle)
                */
        setStories(data);
        setLoading(false);
        sessionStorage.setItem(`stories_${searchTerm}`, JSON.stringify(data));
      } catch (error) {
        console.error(error);
      }
    }, 1200);
    return () => clearTimeout(getStories);
  }, [url, searchTerm]);

  return { stories, loading };
};

export default useStories;
